import { useEffect, useState } from 'react';

import { useSelector } from 'react-redux';

import { PAGE_TYPE_PROPERTY } from 'constants/index';
import { useCountries } from 'hooks/useCountries';
import { useSnackbar } from 'hooks/useSnackbar';
import useValidationSchema from 'hooks/useValidationSchema';
import { pageDataSelector } from 'store/page/selectors';
import { isOwnerSelector, isSuperAdminSelector, accountIdSelector } from 'store/user/selectors';
import { fetchPortfolios } from 'utils/fetchers';

import { usePageContext } from '../../shared/context';
import { useAreasRequest } from '../../shared/hooks/useAreasRequest';
import { useSaveErrorHandler } from '../../shared/hooks/useSaveErrorHandler';
import { useHandleAfterNextDetails } from '../../utils';
import validationSchema from './validationSchema';

export const formatOwnerLabel = (account) => {
  return [account.companyName, account.ownerEmail].filter(Boolean).join(' ');
};

export function usePropertyDetails() {
  const isSuperAdmin = useSelector(isSuperAdminSelector);
  const isOwner = useSelector(isOwnerSelector);
  const page = useSelector(pageDataSelector);
  const accId = useSelector(accountIdSelector);
  const [portfolios, setPortfolios] = useState([]);
  const { areas, areasBeingLoaded, onCountryChange } = useAreasRequest({
    targetId: page.propertyId,
    pageType: PAGE_TYPE_PROPERTY,
    countryId: page.countryId,
  });

  useEffect(() => {
    fetchPortfolios().then(setPortfolios);
  }, [accId, page]);

  const validate = useValidationSchema(validationSchema);
  const countries = useCountries();

  const createPortfolioOption = (label, form) => {
    setPortfolios([{ label, value: label }, ...portfolios]);
    form.change('portfolio', label);
  };

  return {
    isSuperAdmin,
    isOwner,
    page,
    areas,
    areasBeingLoaded,
    onCountryChange,
    validate,
    countries,
    createPortfolioOption,
    portfolios,
  };
}

export function useDetailsFormHandlers() {
  const onSnackbar = useSnackbar();
  const page = useSelector(pageDataSelector);
  const handleSaveError = useSaveErrorHandler();
  const isPageCreating = !page.id;
  const onAfterNext = useHandleAfterNextDetails();

  const handleAfterSave = (page) => {
    if (page?.account?.type === 'owner') {
      onSnackbar(`The content was successfully assigned to '${formatOwnerLabel(page.account)}'.`);
    }
  };

  const handleAfterNext = (page, params) => {
    handleAfterSave(page);
    onAfterNext(page, params);
  };

  const { useNextStepFormHandler } = usePageContext();
  const handleNextStep = useNextStepFormHandler({ onAfterNext: handleAfterNext, onNextError: handleSaveError });

  return {
    isPageCreating,
    handleNextStep,
    handleAfterSave,
    handleSaveError,
  };
}
